import { Link } from "react-router";
import { listCollection, readJson } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import { SITE } from "../lib/site.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import LoopVideo from "../components/LoopVideo/LoopVideo.jsx";
import CompareSlider from "../components/CompareSlider/CompareSlider.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import TestimonialGrid from "../components/TestimonialGrid/TestimonialGrid.jsx";
import CaseStudyShowcase from "../components/CaseStudyShowcase/CaseStudyShowcase.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import { useTabs } from "../lib/useTabs.js";
import styles from "./visualization.module.css";

const PAGE = {
  title: "3D Visualization",
  description:
    "Your infrastructure design, rebuilt as a photorealistic Unreal Engine model and streamed from the cloud to any browser — no CAD seat, no graphics card, nothing to install.",
  path: "/3d-visualization",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "3D Visualization", path: PAGE.path },
];

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/case-studies/henderson-interchange/hero",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

export async function loader() {
  const dots = readJson("clients").filter((c) => c.type === "dot").length;
  // The quotes on this page are about seeing the model, not about the audit.
  const wanted = ["Dianne Wilson", "Nicole Williams", "Richard Armstrong"];
  const testimonials = readJson("testimonials")
    .filter((t) => wanted.includes(t.author))
    .sort((a, b) => wanted.indexOf(a.author) - wanted.indexOf(b.author));
  // The case studies built on a 3D Visualization model.
  const caseStudies = listCollection("case-studies").filter((e) => e.product === "123bim");
  return { dots, testimonials, caseStudies };
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["path", "outputs", "proof"];
const surface = surfaceFor(LIGHT_SECTIONS);

const STEPS = [
  {
    title: "Send the design",
    text: "DGN, DWG, OpenRoads, InfraWorks, Civil 3D or SketchUp, with the survey, terrain and aerial imagery you already hold. No re-drawing, no special export.",
  },
  {
    title: "We rebuild it in 3D",
    text: "The engineered geometry becomes real geometry: pavement and markings, structures, barriers, signs, lighting, terrain and planting, at the level of detail the project calls for.",
  },
  {
    title: "Unreal Engine lights it",
    text: "Real-time lighting, materials, sky and weather, moving traffic and people. The same model at 8am in June and at dusk in December, because both are questions people ask.",
  },
  {
    title: "The cloud runs it",
    text: "An invited viewer signs in and a GPU session starts for them, streaming the model to their browser. Nothing to install, no workstation, no graphics card at the far end.",
  },
];

const CAPABILITIES = [
  "Drive the alignment at the posted speed, walk the sidewalk, fly the corridor",
  "Change the time of day, the season and the weather",
  "Turn layers on and off: proposed against existing, or one construction phase",
  "Saved viewpoints, so a presenter jumps straight to the four views that matter",
  "A guided tour the people you invite follow at their own pace, in their own time",
];

// What comes out of the one model, by the form it takes, one tab each.
const OUTPUT_GROUPS = [
  {
    title: "Video and stills",
    media: {
      src: "/images/site/123bim/photo-stage",
      alt: "A photorealistic still of a bridge under construction, with traffic on the open lanes",
      caption: "A construction stage, rendered from the model the video comes from.",
    },
    items: [
      [
        "Drive-throughs and flyovers",
        "HD video along any route in the model, at road level or from the air.",
      ],
      [
        "Phasing sequences",
        "Each construction phase in order: detours, closures and temporary alignments.",
      ],
      ["Stills", "For display boards, slide decks and press, from the same model as the video."],
    ],
  },
  {
    title: "The live model",
    media: {
      src: "/images/site/123bim/model",
      alt: "The model open in a browser, with a collaboration panel listing the people in the session",
      caption: "The model in a browser tab, with everyone in the session listed beside it.",
    },
    items: [
      [
        "In any browser",
        "An invited viewer opens a link and a cloud GPU streams the model to them.",
      ],
      ["Saved viewpoints", "A presenter jumps straight to the views that matter."],
      ["Guided tours", "Reviewers follow them at their own pace, in their own time."],
      ["Who opened it", "Every session is by invitation, so you can see who has looked."],
    ],
  },
  {
    title: "At the open house",
    media: {
      src: "/images/site/123bim/tour-any-device",
      alt: "The virtual tour of an interchange rendered once for each eye, as a VR headset shows it",
      caption: "The virtual tour in a headset: one view for each eye.",
    },
    items: [
      [
        "In a headset",
        "VR sessions at the back of the room, standing on the road that will be built.",
      ],
      [
        "On the big screen",
        "The model projected while the animation plays, stopping for the view someone asks for.",
      ],
      [
        "On any device",
        "The virtual tour opens on a phone, a tablet or a computer, for everyone who couldn't come.",
      ],
    ],
  },
];

const USES = [
  {
    title: "Public hearings",
    text: "Presented live in the room or in a headset at the back of it, residents see the road that will be built from their own street instead of reading a plan sheet. Objections come back about the design, not about what people imagine it to be.",
  },
  {
    title: "Design review",
    text: "Reviewers drive the alignment and look at the conflict everyone is arguing about, from the driver's seat, in the meeting.",
  },
  {
    title: "Alternatives",
    text: "Switch between alternatives in the same session and on the same terrain, so a preference is a choice between two things people have actually seen.",
  },
  {
    title: "Construction staging",
    text: "Walk each phase before the first cone goes out: detours, closures, temporary alignments and the sequence the public will live through.",
  },
  {
    title: "Funding and partners",
    text: "A model a commission, a city council or a funding partner can navigate themselves does more than a rendering does.",
  },
  {
    title: "Safety, on the same model",
    text: "The model feeds Road Safety Audit 3D, so the design can be audited for sight distance while it is still a design.",
  },
];

/** The model's outputs by the form they take: each panel pairs a picture with the list. */
function OutputTabs({ groups }) {
  const { tabProps, panelProps } = useTabs(groups.length);
  return (
    <div>
      <div
        className={`segmented ${styles.outputTabs}`}
        role="tablist"
        aria-label="What the model delivers"
      >
        {groups.map((group, i) => (
          <button key={group.title} {...tabProps(i)}>
            {group.title}
          </button>
        ))}
      </div>
      {groups.map((group, i) => (
        <div key={group.title} {...panelProps(i)} className={styles.outputPanel}>
          <figure className={styles.outputMedia}>
            <Picture
              src={group.media.src}
              alt={group.media.alt}
              widths={[480, 960, 1600]}
              fallbackWidth={960}
              sizes="(min-width: 64rem) 55vw, 100vw"
              className={styles.outputImage}
            />
            <figcaption className={styles.outputCaption}>{group.media.caption}</figcaption>
          </figure>
          <ul className={styles.outputList}>
            {group.items.map(([name, text]) => (
              <li key={name}>
                <strong>{name}</strong>
                {text}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function Visualization({ loaderData }) {
  const { dots, testimonials, caseStudies } = loaderData;
  return (
    <>
      <section className={styles.hero} aria-labelledby="viz-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="viz-title" className={styles.heroTitle}>
              Let everyone see the project before it&apos;s built
            </h1>
            <p className={styles.heroLead}>
              We rebuild your design from the CAD files you already have as a photorealistic,
              geometrically accurate Unreal Engine model, then stream it from cloud GPUs to any
              browser. No CAD seat, no graphics card, nothing to install.
            </p>
            <div className={styles.heroActions}>
              <Link to="/demo" className="btn btn-primary">
                Request a demo
              </Link>
              <a href="#compare" className={styles.heroLink}>
                Compare existing and proposed
              </a>
            </div>
            <p className={styles.heroNote}>
              Built from DGN, DWG, OpenRoads, InfraWorks, Civil 3D or SketchUp files, with the
              survey, terrain and imagery you already hold.
            </p>
            <p className={styles.heroNote}>
              From RDV Systems, trusted by {dots} state DOTs since {SITE.foundedYear}.
            </p>
          </div>
          <div className={styles.heroMedia}>
            <LoopVideo
              src="/videos/visualization-loop.mp4"
              poster="/images/site/home/visualization-loop.jpg"
              caption="An intersection design, rendered as a 3D model for a public meeting"
              ratio="16 / 11"
            />
            <p className={styles.heroChip}>Rendered in Unreal Engine</p>
          </div>
        </div>
      </section>

      {/* The page's one interactive moment: the design where it will stand, against what's there. */}
      <section className="section" id="compare" aria-labelledby="compare-title">
        <div className="container">
          <SectionHeader
            id="compare-title"
            title="See the design where it will stand"
            aside="The Frank J. Wood Bridge near Brunswick, Maine, modeled as it stood and as a replacement was proposed, from one viewpoint in one model. Drag across the picture to compare."
            link={{ to: "/123bim/3d-modeling", label: "How existing conditions are modeled" }}
          />
          <CompareSlider
            label="Compare the existing bridge with the proposed replacement"
            before={{
              src: "/images/site/123bim/modeling-existing",
              alt: "The existing steel truss bridge, modeled with the riverbanks and mill buildings around it",
              label: "Existing",
            }}
            after={{
              src: "/images/site/123bim/modeling-design",
              alt: "The proposed replacement bridge in the same view, with curved walls and planting where it meets the bank",
              label: "Proposed",
            }}
          />
        </div>
      </section>

      <section className="section" data-surface={surface("path")} aria-labelledby="path-title">
        <div className="container">
          <SectionHeader
            id="path-title"
            title="From design files to a live model, in four steps"
            aside="You send the files you already have. Everything after that is ours."
          />
          <ol className={styles.steps}>
            {STEPS.map((step, i) => (
              <li key={step.title}>
                <span className={styles.stepNo} aria-hidden="true">
                  {i + 1}
                </span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section" aria-labelledby="inside-title">
        <div className={`container ${styles.inside}`}>
          <Picture
            src="/images/site/123bim/photo-morning"
            alt="A photorealistic view along a proposed bridge at sunrise, with traffic in both directions"
            widths={[480, 960, 1600]}
            fallbackWidth={960}
            sizes="(min-width: 64rem) 55vw, 100vw"
            className={styles.insideImage}
          />
          <div className={styles.insideText}>
            <h2 id="inside-title">A model you drive, not a video you watch</h2>
            <p className={styles.insideLead}>
              A rendering shows one viewpoint that someone chose. A real-time model answers the
              question being asked in the room, while it is being asked.
            </p>
            <ul className={styles.capabilities}>
              {CAPABILITIES.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        className="section"
        data-surface={surface("outputs")}
        aria-labelledby="outputs-title"
      >
        <div className="container">
          <SectionHeader
            id="outputs-title"
            title="One model, every deliverable the project needs"
            aside="The model is the source, so the video, the boards and the link never show three different designs. When the design changes, it is updated in place."
          />
          <OutputTabs groups={OUTPUT_GROUPS} />
        </div>
      </section>

      <section className="section" aria-labelledby="uses-title">
        <div className="container">
          <SectionHeader
            id="uses-title"
            title="What DOTs and firms use it for"
            aside="Anywhere a decision waits on people seeing the same thing."
          />
          <ul className={styles.uses}>
            {USES.map((use) => (
              <li key={use.title} className={styles.use}>
                <h3 className={styles.useTitle}>{use.title}</h3>
                <p>{use.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section
        className="section"
        data-surface={surface("proof")}
        aria-labelledby="viz-quotes-title"
      >
        <div className="container">
          <SectionHeader
            id="viz-quotes-title"
            title="What it does to a public meeting"
            aside="In the words of the people who ran the meetings."
            link={{ to: "/about/testimonials", label: "All testimonials" }}
          />
          <TestimonialGrid items={testimonials} stats />
        </div>
      </section>

      {caseStudies.length ? (
        <section className="section" aria-labelledby="viz-work-title">
          <div className="container">
            <SectionHeader
              id="viz-work-title"
              title="Built for public meetings"
              aside="Interchanges modeled from their design files, then shown to the public."
              link={{ to: "/case-studies", label: "All case studies" }}
            />
            <CaseStudyShowcase items={caseStudies} label="Visualization case studies" />
          </div>
        </section>
      ) : null}

      <CTABand
        variant="statement"
        image="/images/site/123bim/photo-brunswick"
        title="See your project in Unreal Engine"
        text="Send a DGN, DWG, OpenRoads or Civil 3D file and get a link to your own model."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
